import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import useSEO from '@/hooks/useSEO';
import { Phone, Mail, MapPin } from 'lucide-react';
import AuditForm from '@/components/AuditForm';

// Section component with scroll animation
const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.7 }}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const ContactPage = () => {
  useSEO({
    title: 'Contact Conciergerie Confort Hôte',
    description: 'Contactez Confort Hôte, conciergerie Airbnb à Fougères et Rennes, Vitré, Dol-de-Bretagne, Pleine-Fougères. Demandez un accompagnement pour la gestion de votre location courte durée.',
    keywords: 'contact conciergerie Airbnb, conciergerie, confort hôte, Rennes, conciergerie Vitré, conciergerie Dol-de-Bretagne, conciergerie Pleine-Fougères, Confort Hôte',
  });

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left - Info */}
            <div>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-6"
              >
                Contact
              </motion.p>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-4xl sm:text-5xl lg:text-6xl font-playfair font-medium tracking-tight leading-[1.1] text-[#F5F5F5] mb-8"
              >
                Demandez votre<br/>
                <span className="text-gold-gradient">audit gratuit</span>
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-[#A3A3A3] font-light leading-relaxed mb-12"
              >
                Obtenez une estimation personnalisée du potentiel locatif de votre bien. 
                Notre équipe vous rappelle sous 24h.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="space-y-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 text-[#C5A059]">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Téléphone</p>
                    <a 
                      href="tel:0689224402" 
                      className="text-xl text-[#F5F5F5] hover:text-[#C5A059] transition-colors duration-300"
                      data-testid="contact-phone"
                    >
                      06 89 22 44 02
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 text-[#C5A059]">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Email</p>
                    <a 
                      href="mailto:contact@confort-hote.fr" 
                      className="text-xl text-[#F5F5F5] hover:text-[#C5A059] transition-colors duration-300"
                      data-testid="contact-email"
                    >
                      contact@confort-hote.fr
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#C5A059]/10 text-[#C5A059]">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-[#A3A3A3] mb-1">Zone d'intervention</p>
                    <p className="text-xl text-[#F5F5F5]">
                      Ille-et-Vilaine · Bretagne
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right - Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
            >
              <div className="glass-card p-8 md:p-12">
                <AuditForm />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <Section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-playfair text-[#C5A059] mb-4">24h</p>
              <p className="text-[#A3A3A3] font-light">Délai de réponse garanti</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-playfair text-[#C5A059] mb-4">100%</p>
              <p className="text-[#A3A3A3] font-light">Gratuit et sans engagement</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-playfair text-[#C5A059] mb-4">1</p>
              <p className="text-[#A3A3A3] font-light">Interlocuteur dédié</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default ContactPage;
